import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SelectContextValue {
  value: string;
  onValueChange: (value: string) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** value -> the SelectItem's rendered children, so SelectValue can show the
   * resolved label instead of the raw value when no explicit children are
   * passed to SelectValue. Built by walking the full children tree (not just
   * what's mounted) so it's populated even while SelectContent is closed. */
  itemLabels: Map<string, React.ReactNode>;
}

const SelectContext = React.createContext<SelectContextValue | null>(null);

/** Recursively walks a children tree looking for SelectItem elements,
 * regardless of how deeply they're nested (e.g. wrapped in fragments or
 * grouping components), and records value -> label. */
function collectItemLabels(children: React.ReactNode, map: Map<string, React.ReactNode>): void {
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    if (child.type === SelectItem) {
      const itemProps = child.props as SelectItemProps;
      map.set(itemProps.value, itemProps.children);
      return;
    }
    const nested = (child.props as { children?: React.ReactNode } | null)?.children;
    if (nested) collectItemLabels(nested, map);
  });
}

interface SelectProps {
  value?: string;
  onValueChange?: (value: string) => void;
  children: React.ReactNode;
}

const Select = ({ value = '', onValueChange, children }: SelectProps) => {
  const [open, setOpen] = React.useState(false);

  const itemLabels = React.useMemo(() => {
    const map = new Map<string, React.ReactNode>();
    collectItemLabels(children, map);
    return map;
  }, [children]);

  const contextValue: SelectContextValue = {
    value,
    onValueChange: onValueChange || (() => {}),
    open,
    onOpenChange: setOpen,
    itemLabels,
  };

  return (
    <SelectContext.Provider value={contextValue}>
      <div className="relative">{children}</div>
    </SelectContext.Provider>
  );
};

interface SelectTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
}

const SelectTrigger = React.forwardRef<HTMLButtonElement, SelectTriggerProps>(
  ({ className, children, ...props }, ref) => {
    const context = React.useContext(SelectContext);
    if (!context) throw new Error('SelectTrigger must be used within Select');

    return (
      <button
        ref={ref}
        type="button"
        className={cn(
          'flex h-9 w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50',
          className
        )}
        onClick={() => context.onOpenChange(!context.open)}
        {...props}
      >
        <div className="flex-1 text-left">
          {children}
        </div>
        <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0" />
      </button>
    );
  }
);
SelectTrigger.displayName = 'SelectTrigger';

interface SelectValueProps {
  placeholder?: string;
  children?: React.ReactNode;
}

const SelectValue = ({ placeholder, children }: SelectValueProps) => {
  const context = React.useContext(SelectContext);
  if (!context) throw new Error('SelectValue must be used within Select');

  // If children are provided, use them for custom rendering
  if (children) {
    return <>{children}</>;
  }

  // Resolve the selected SelectItem's label so the trigger shows readable
  // text instead of a raw id/UUID value. Falls back to the raw value if no
  // matching SelectItem is found (e.g. options loaded asynchronously after
  // the value was set), preserving the previous behavior in that edge case.
  const resolvedLabel = context.value ? context.itemLabels.get(context.value) ?? context.value : undefined;

  return <span>{resolvedLabel || placeholder}</span>;
};

interface SelectContentProps {
  children: React.ReactNode;
}

const SelectContent = ({ children }: SelectContentProps) => {
  const context = React.useContext(SelectContext);
  if (!context) throw new Error('SelectContent must be used within Select');

  if (!context.open) return null;

  return (
    <div className="absolute top-full z-50 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg">
      <div className="max-h-60 overflow-auto p-1">{children}</div>
    </div>
  );
};

interface SelectItemProps {
  value: string;
  children: React.ReactNode;
}

const SelectItem = ({ value, children }: SelectItemProps) => {
  const context = React.useContext(SelectContext);
  if (!context) throw new Error('SelectItem must be used within Select');

  const handleSelect = () => {
    context.onValueChange(value);
    context.onOpenChange(false);
  };

  return (
    <div
      className={cn(
        'relative flex cursor-pointer select-none items-center rounded-md px-2 py-1.5 text-sm text-gray-900 outline-none hover:bg-gray-100 focus:bg-gray-100',
        context.value === value && 'bg-blue-50 text-blue-700'
      )}
      onClick={handleSelect}
    >
      {children}
    </div>
  );
};

export { Select, SelectTrigger, SelectValue, SelectContent, SelectItem };
